
import { supabase } from "@/integrations/supabase/client";

// Check if a listing has active premium status
export const checkPremiumStatus = async (
  listingId: number
): Promise<boolean> => {
  try {
    const now = new Date().toISOString();

    const { data, error } = await supabase
      .from("premium_payments")
      .select("id")
      .eq("listing_id", listingId)
      .gte("expires_at", now) // Change to check expires_at instead of created_at
      .limit(1);

    if (error) throw error;

    return data && data.length > 0;
  } catch (error) {
    console.error("Error checking premium status:", error);
    return false;
  }
};

// Record a premium payment
export const recordPremiumPayment = async (
  listingId: number,
  userId: string,
  fromVoucher: boolean = false
): Promise<boolean> => {
  try {
    // Calculate expiration date - 28 days from now
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + 28);

    const { error } = await supabase.from("premium_payments").insert({
      listing_id: listingId,
      user_id: userId,
      from_voucher: fromVoucher,
      expires_at: expiresAt.toISOString(), // Add expires_at field
    });

    if (error) throw error;

    const { error: updateError } = await supabase
      .from("listings")
      .update({ is_premium: true })
      .eq("id", listingId);

    if (updateError) throw updateError;

    return true;
  } catch (error) {
    console.error("Error recording premium payment:", error);
    return false;
  }
};

// Get user's free premium vouchers from premium_payments
export const getUserPremiumVouchers = async (
  userId: string
): Promise<number> => {
  try {
    type PremiumVoucherResult = {
      id: string;
      from_voucher: boolean;
      is_used: boolean;
    }[];

    const { data, error } = await supabase
      .from("premium_payments")
      .select("*")
      .eq("user_id", userId)
      .eq("from_voucher", true)
      .eq("is_used", false);

    if (error) throw error;

    return (data as PremiumVoucherResult)?.length || 0;
  } catch (error) {
    console.error("Error getting premium vouchers:", error);
    return 0;
  }
};

// Use a premium voucher for a listing
export const usePremiumVoucher = async (
  listingId: number,
  userId: string
): Promise<boolean> => {
  try {
    type PremiumVoucherResult = {
      id: string;
      from_voucher: boolean;
      is_used: boolean;
    }[];

    // Get an available voucher
    const { data, error } = await supabase
      .from("premium_payments")
      .select("id")
      .eq("user_id", userId)
      .eq("from_voucher", true)
      .eq("is_used", false)
      .limit(1);

    if (error || !data || data.length === 0) {
      return false;
    }

    const voucherId = (data as PremiumVoucherResult)[0]?.id;
    if (!voucherId) return false;

    // Calculate expiration date - 28 days from now
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + 28);

    // Mark the selected voucher as used and assign it to the listing
    const { error: updateError } = await supabase
      .from("premium_payments")
      .update({
        is_used: true,
        listing_id: listingId,
        expires_at: expiresAt.toISOString(), // Set expires_at field
      })
      .eq("id", voucherId);

    if (updateError) {
      throw updateError;
    }

    // Update the listing to premium status
    const { error: listingError } = await supabase
      .from("listings")
      .update({ is_premium: true })
      .eq("id", listingId);

    if (listingError) {
      throw listingError;
    }

    return true;
  } catch (error) {
    console.error("Error using premium voucher:", error);
    return false;
  }
};

// Get premium listing expiration date
export const getPremiumExpirationDate = async (
  listingId: number
): Promise<Date | null> => {
  try {
    const { data, error } = await supabase
      .from("premium_payments")
      .select("expires_at") // Use expires_at instead of calculating from created_at
      .eq("listing_id", listingId)
      .order("expires_at", { ascending: false }) // Order by expires_at
      .limit(1);

    if (error) {
      console.error("Error fetching premium expiration date:", error);
      throw error;
    }

    if (!data || data.length === 0) {
      console.warn(`No premium payment found for listing ${listingId}`);
      return null;
    }

    // Log the date for debugging
    console.log(`Premium expires at: ${data[0].expires_at} for listing ${listingId}`);
    
    if (!data[0].expires_at) {
      console.warn(`Expiration date is null for listing ${listingId}`);
      return null;
    }

    return new Date(data[0].expires_at);
  } catch (error) {
    console.error("Error in getPremiumExpirationDate:", error);
    throw error;
  }
};
